import { useState } from 'react';
import { useCachedSelectedProject, useTranslation } from 'hooks';
import { RuleCard } from 'shared/ui/rule-card/rule-card';
import { RuleDrawer } from 'shared/ui/rule-drawer/rule-drawer';
import { track } from 'utils/mixpanel';
import { getRuleTrackName } from 'widgets/score-disclosure/helpers';
import { InfoCompletenessScoring } from 'store/api/apiTypes';
import { getScoreDisplayConfig } from 'shared/helpers/ruleScoring';

interface AnalysisRuleItemProps {
  title: string;
  rule: InfoCompletenessScoring;
  ruleDescription: React.ReactNode;
  index?: number;
  skipRestrictions?: boolean;
  trackName?: string;
  isTechnicalAccessibility?: boolean;
  maxScore?: number;
}

export const AnalysisRuleItem: React.FC<AnalysisRuleItemProps> = ({
  title,
  rule,
  ruleDescription,
  index,
  skipRestrictions = false,
  trackName,
  isTechnicalAccessibility = false,
  maxScore,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const { isTrial, isTrialExpired, testingB } = useCachedSelectedProject();

  const restricted = skipRestrictions
    ? false
    : (index !== 0 && testingB && isTrial) || isTrialExpired;
  const ruleTrackName = getRuleTrackName(title);

  const handleOpen = () => {
    setIsOpen(true);
    track('AIS:DSH_CRD_ANALYSIS-OPENED', {
      is_trial: isTrial,
      type: trackName,
      sub_type: ruleTrackName,
    });
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const statusConfig = isTechnicalAccessibility
    ? getScoreDisplayConfig(rule.score, maxScore)
    : undefined;
  const chipLabel = statusConfig ? t(statusConfig.labelKey) : undefined;
  const chipColor = statusConfig?.color;

  return (
    <>
      <RuleCard
        title={title}
        description={restricted ? undefined : rule.reasoning}
        variant='analysis'
        analysisScore={rule.score}
        maxScore={maxScore}
        restricted={restricted}
        isTrialExpired={isTrialExpired}
        onOpen={handleOpen}
        chipContent={chipLabel}
        chipColor={chipColor}
      />
      <RuleDrawer
        isOpen={isOpen}
        onClose={handleClose}
        title={title}
        description={rule.reasoning}
        variant='analysis'
        analysisScore={rule.score}
        analysisRule={rule}
        ruleDescriptionContent={ruleDescription}
        typeName={trackName}
        isTechnicalAccessibility={isTechnicalAccessibility}
        maxScore={maxScore}
      />
    </>
  );
};
